import { Badge, Group, LoadingOverlay, Pagination, ScrollArea, Table, Text } from "@mantine/core";
import { List, ShowButton } from "@refinedev/mantine";
import { OperationsBar } from "components/common/OperationsBar";
import { useTable } from "@refinedev/react-table";
import { ColumnDef } from "@tanstack/react-table";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import {
  TeaAddonRenewalRun
} from "entities/tea_addon_renewal_run";
import { useMemo } from "react";
import { format } from "date-fns";
import { Row } from "react-table";

export const TeaAddonRenewalRunList: React.FC = () => {
  const columns = useMemo<ColumnDef<TeaAddonRenewalRun>[]>(
    () => [
      {
        id: "id",
        header: "ID",
        accessorKey: "id",
        cell: function render({ getValue }) {
          const id = getValue<TeaAddonRenewalRun["id"]>();
          return <Badge variant="outline" color="dark">#{id}</Badge>;
        },
      },
      {
        id: "range_start_at",
        header: "Range Start At",
        accessorKey: "range_start_at",
        cell: function render({ getValue }) {
          const date = getValue<TeaAddonRenewalRun["range_start_at"]>();
          if (!date) return <Badge color="gray" variant="light">—</Badge>;
          return <Text size="sm">{format(new Date(date), "dd MMM yyyy HH:mm")}</Text>;
        },
      },
      {
        id: "range_end_at",
        header: "Range End At",
        accessorKey: "range_end_at",
        cell: function render({ getValue }) {
          const date = getValue<TeaAddonRenewalRun["range_end_at"]>();
          if (!date) return <Badge color="gray" variant="light">—</Badge>;
          return <Text size="sm">{format(new Date(date), "dd MMM yyyy HH:mm")}</Text>;
        },
      },
      {
        id: "started_at",
        header: "Started At",
        accessorKey: "started_at",
        cell: function render({ getValue }) {
          const date = getValue<TeaAddonRenewalRun["started_at"]>();
          if (!date) return <Badge color="gray" variant="light">—</Badge>;
          return <Text size="sm">{format(new Date(date), "dd MMM yyyy HH:mm")}</Text>;
        },
      },
      {
        id: "ended_at",
        header: "Ended At",
        accessorKey: "ended_at",
        cell: function render({ getValue }) {
          const date = getValue<TeaAddonRenewalRun["ended_at"]>();
          if (!date) return <Badge color="gray" variant="light">—</Badge>;
          return <Text size="sm">{format(new Date(date), "dd MMM yyyy HH:mm")}</Text>;
        },
      },
      {
        id: "operations_distribution",
        header: "Operations",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: function render({ row }) {
          const r = row.original as TeaAddonRenewalRun;
          const succeeded = r.succeeded_operations_count || 0;
          const failed = r.failed_operations_count || 0;
          const canceled = r.canceled_operations_count || 0;
          return (
            <OperationsBar
              width={100}
              height={10}
              segments={[
                { value: failed, color: "#E53935", label: "failed" },
                { value: succeeded, color: "#43A047", label: "succeeded" },
                { value: canceled, color: "#FB8C00", label: "canceled" },
              ]}
            />
          );
        },
      },
      {
        id: "created_at",
        header: "Created At",
        accessorKey: "created_at",
        cell: function render({ getValue }) {
          const date = getValue<TeaAddonRenewalRun["created_at"]>();
          if (!date) return <Badge color="gray" variant="light">—</Badge>;
          return <Text size="sm">{format(new Date(date), "dd MMM yyyy HH:mm")}</Text>;
        },
      },
      {
        id: "actions",
        header: "Actions",
        accessorKey: "id",
        cell: function render({ getValue }) {
          return (
            <Group spacing="xs" noWrap>
              <ShowButton hideText recordItemId={getValue() as number} />
            </Group>
          );
        },
      },
    ],
    [],
  );

  const {
    getHeaderGroups,
    getRowModel,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ],
      },
      pagination: {
        pageSize: 50,
      },
    },
  });

  return (
    <List>
      <AppliedFilters<TeaAddonRenewalRun> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<TeaAddonRenewalRun> headerGroups={getHeaderGroups()} />
          <Rows<TeaAddonRenewalRun>
            rows={getRowModel().rows}
            rowStyle={(row: Row<TeaAddonRenewalRun>) =>
              row.original.failed_operations_count !== 0
                ? { background: "rgba(198, 40, 40, 0.1)" }
                : {}
            }
          />
        </Table>
      </ScrollArea>
      <br />
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
